import { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Collapse,
  InputAdornment,
  IconButton,
  TableSortLabel,
  Button,
  ButtonGroup,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";

const TAX_RATE = 0.07;

const STATUS_OPTIONS = [
  { label: "ส่งแล้ว", value: "sent" },
  { label: "ยืนยันแล้ว", value: "confirm" },
  { label: "รับเงินแล้ว", value: "payed" },
];

export default function DailyPurchaseSummary() {
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // sort state
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");

  // status filter state
  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0].value);

  const cellStyle = { border: "1px solid #ddd" };

  // ---------- Load sales data ----------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getSaleTransactions({
          ...(selectedStatus ? { status: selectedStatus } : {}),
          startDate,
          endDate,
        });

        const rawData = res?.data?.data ?? res?.data ?? res ?? [];

        const processed = (Array.isArray(rawData) ? rawData : [rawData]).map(
          (row) => {
            const isVat = row?.isVat === true;

            const items = (row.items ?? []).map((item) => {
              // น้ำหนักปลายทางสุทธิของรายการ
              const destWeight = Number(item.netWeight ?? item.weight ?? 0);

              // น้ำหนักส่งสุทธิของรายการ (ถ้าไม่มี field sendWeight จะ fallback = destWeight)
              const sendWeight = Number(item.sendWeight ?? destWeight);

              // คำนวณเงิน
              const pricePerUnit = Number(item.pricePerUnit ?? 0);
              const confirmPricePerUnit = Number(item.confirmPricePerUnit ?? 0);

              const amountBeforeVat =
                Number(item.amountBeforeVat ??
                  destWeight * pricePerUnit);

              // VAT ต่อรายการ: ถ้าบิลมี vat รวม จะไปกระจายทีหลัง
              const itemVat =
                !isVat ? 0 :
                  row.vat != null ? 0 :
                    amountBeforeVat * TAX_RATE;

              return {
                ...item,
                productName: String(item.productName ?? item.product?.title ?? "-").trim(),
                netWeight: destWeight,          // น้ำหนักปลายทางสุทธิ
                sendWeight,                     // น้ำหนักส่งสุทธิ
                pricePerUnit,
                confirmPricePerUnit,
                amountBeforeVat,
                vat: itemVat,
                netPrice: Number(item.netPrice ?? amountBeforeVat + itemVat),
              };
            });

            // กระจาย VAT ระดับบิล
            if (isVat && row.vat != null && items.length) {
              const totalBefore = items.reduce(
                (acc, it) => acc + Number(it.amountBeforeVat || 0),
                0
              );
              const totalVatFromRow = Number(row.vat ?? 0);
              items.forEach((it) => {
                const share =
                  totalBefore > 0
                    ? (Number(it.amountBeforeVat || 0) / totalBefore) * totalVatFromRow
                    : 0;
                it.vat = share;
                it.netPrice = Number(it.amountBeforeVat || 0) + share;
              });
            }

            return { ...row, items };
          }
        );

        setRows(processed);
        setOpenSet(new Set());
      } catch (err) {
        console.error("fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, selectedStatus]);

  // ---------- Utils ----------
  const formatNum = (val) =>
    val != null
      ? Number(val).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      : "-";

  const formatDateTime = (datetimeStr) => {
    const date = new Date(datetimeStr);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatThaiDate = (datetimeStr) => {
    const date = new Date(datetimeStr);
    return date.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // ---------- Row metrics (sum จาก sub items) ----------
  const getRowMetrics = (row) => {
    const weightSend =
      row.items?.reduce(
        (acc, it) => acc + Number(it.sendWeight ?? it.netWeight ?? 0),
        0
      ) ?? 0;

    const weightDest =
      row.items?.reduce(
        (acc, it) => acc + Number(it.netWeight ?? 0),
        0
      ) ?? 0;

    const weightDeduct =
      row.items?.reduce((acc, it) => {
        const sendW = Number(it.sendWeight ?? it.netWeight ?? 0);
        const destW = Number(it.netWeight ?? 0);
        return acc + (sendW - destW);
      }, 0) ?? 0;

    const beforeVat =
      row.items?.reduce(
        (acc, it) => acc + Number(it.amountBeforeVat ?? 0),
        0
      ) ?? 0;

    const vat =
      row.items?.reduce(
        (acc, it) => acc + Number(it.vat ?? 0),
        0
      ) ?? 0;

    const grandTotal = beforeVat + vat;

    return {
      weightSend,    // น้ำหนักส่งสุทธิ (หลัก) = sum(sub)
      weightDeduct,  // น้ำหนักหัก (หลัก) = sum(send - dest) ของ sub
      weightDest,    // น้ำหนักปลายทางสุทธิ (หลัก) = sum(sub)
      beforeVat,
      vat,
      grandTotal,
    };
  };

  // ---------- Search ----------
  const filteredRows = useMemo(() => {
    const q = (searchQuery || "").toLowerCase().trim();
    if (!q) return rows;

    return rows.filter((row) => {
      const bill = row.billNumber?.toLowerCase() || "";
      const customer =
        row.customer?.name?.toLowerCase() ||
        row.customerName?.toLowerCase() ||
        "";
      const dateTH = formatThaiDate(row.createdAt).toLowerCase();
      const dateISO = String(row.createdAt || "").toLowerCase();
      const productList = (row.items || [])
        .map((it) => (it.productName || "").toLowerCase())
        .join(" ");

      return (
        bill.includes(q) ||
        customer.includes(q) ||
        dateTH.includes(q) ||
        dateISO.includes(q) ||
        productList.includes(q)
      );
    });
  }, [rows, searchQuery]);

  // ---------- Sort ----------
  const sortedRows = useMemo(() => {
    const data = [...filteredRows];
    data.sort((a, b) => {
      const mA = getRowMetrics(a);
      const mB = getRowMetrics(b);

      let aVal;
      let bVal;

      switch (orderBy) {
        case "billNumber":
          aVal = a.billNumber ?? "";
          bVal = b.billNumber ?? "";
          break;
        case "createdAt":
          aVal = new Date(a.createdAt).getTime() || 0;
          bVal = new Date(b.createdAt).getTime() || 0;
          break;
        case "customerName":
          aVal = (a.customer?.name ?? a.customerName ?? "").toLowerCase();
          bVal = (b.customer?.name ?? b.customerName ?? "").toLowerCase();
          break;
        case "weightSend":
          aVal = mA.weightSend;
          bVal = mB.weightSend;
          break;
        case "weightDeduct":
          aVal = mA.weightDeduct;
          bVal = mB.weightDeduct;
          break;
        case "weightDest":
          aVal = mA.weightDest;
          bVal = mB.weightDest;
          break;
        case "beforeVat":
          aVal = mA.beforeVat;
          bVal = mB.beforeVat;
          break;
        case "vat":
          aVal = mA.vat;
          bVal = mB.vat;
          break;
        case "grandTotal":
          aVal = mA.grandTotal;
          bVal = mB.grandTotal;
          break;
        default:
          aVal = 0;
          bVal = 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal, "th");
        return order === "asc" ? cmp : -cmp;
      } else {
        const cmp = (aVal || 0) - (bVal || 0);
        return order === "asc" ? cmp : -cmp;
      }
    });
    return data;
  }, [filteredRows, orderBy, order]);

  const requestSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      if (["billNumber", "customerName"].includes(key)) setOrder("asc");
      else setOrder("desc");
    }
  };

  const getRowKey = (row, idx) =>
    String(row.transactionId ?? row.id ?? row.billNumber ?? idx);

  const toggleOpen = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ---------- Export (main) ----------
  function exportToCSV(data, filename = "sales-summary.csv") {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleExportMain = () => {
    const dataset = sortedRows.map((row) => {
      const {
        weightSend,
        weightDeduct,
        weightDest,
        beforeVat,
        vat,
        grandTotal,
      } = getRowMetrics(row);
      const orderedBy =
        row.orderedBy?.name ?? row.createdBy?.name ?? row.shop?.name ?? "-";

      return {
        เลขที่Batch: row.billNumber ?? "",
        วันที่จ่าย: formatDateTime(row.createdAt),
        ชื่อซัพพลายเออร์: row.customer?.name ?? row.customerName ?? "-",
        สั่งจ่ายโดย: orderedBy,
        "น้ำหนักส่งสุทธิ": Number(weightSend).toFixed(2),
        "น้ำหนักหัก": Number(weightDeduct).toFixed(2),
        "น้ำหนักปลายทางสุทธิ": Number(weightDest).toFixed(2),
        "ยอดขายก่อนภาษี": Number(beforeVat).toFixed(2),
        "ยอดภาษี": Number(vat).toFixed(2),
        "ยอดรวม": Number(grandTotal).toFixed(2),
      };
    });

    exportToCSV(dataset, `sales-summary_${startDate}_to_${endDate}.csv`);
  };

  // ---------- Summary (footer) ----------
  const summary = useMemo(() => {
    let ticketCount = sortedRows.length;
    let weightSend = 0;
    let weightDeduct = 0;
    let weightDest = 0;
    let totalBeforeVat = 0;
    let totalVat = 0;

    sortedRows.forEach((row) => {
      const m = getRowMetrics(row);
      weightSend += m.weightSend;
      weightDeduct += m.weightDeduct;
      weightDest += m.weightDest;
      totalBeforeVat += m.beforeVat;
      totalVat += m.vat;
    });

    const grandTotal = totalBeforeVat + totalVat;

    return {
      ticketCount,
      weightSend,
      weightDeduct,
      weightDest,
      totalBeforeVat,
      totalVat,
      grandTotal,
    };
  }, [sortedRows]);

  return (
    <Box sx={{ p: 2, backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <AppBreadcrumbs />

      {/* ค้นหา + วันที่ */}
      <Box display="flex" justifyContent="flex-end" gap={2} my={2} flexWrap="wrap">
        <TextField
          label="ค้นหา"
          value={searchQuery}
          placeholder="เลขที่Batch / วันที่จ่าย / ซัพพลายเออร์ / สินค้า"
          onChange={(e) => setSearchQuery(e.target.value)}
          size="medium"
          sx={{ minWidth: 300, maxWidth: 520 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="เริ่มต้น"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="สิ้นสุด"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* หัวเรื่อง + ปุ่มโหลด + ตัวกรองสถานะ */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            my={1}
            gap={2}
            flexWrap="wrap"
          >
            <Typography variant="h6" gutterBottom>
              สรุปยอดขายสินค้ารายBatch
            </Typography>

            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <ButtonGroup variant="outlined" aria-label="status filter">
                {STATUS_OPTIONS.map((opt) => (
                  <Button
                    key={opt.value || "all"}
                    size="large"
                    onClick={() => setSelectedStatus(opt.value)}
                    variant={selectedStatus === opt.value ? "contained" : "outlined"}
                  >
                    {opt.label}
                  </Button>
                ))}
              </ButtonGroup>

              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                size="large"
                onClick={handleExportMain}
              >
                โหลดข้อมูล
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                <TableRow>
                  <TableCell sx={{ ...cellStyle, padding: "10px" }} />
                  <TableCell
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "billNumber" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "billNumber"}
                      direction={orderBy === "billNumber" ? order : "asc"}
                      onClick={() => requestSort("billNumber")}
                    >
                      เลขที่Batch
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "createdAt" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "createdAt"}
                      direction={orderBy === "createdAt" ? order : "desc"}
                      onClick={() => requestSort("createdAt")}
                    >
                      วันที่จ่าย
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "customerName" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "customerName"}
                      direction={orderBy === "customerName" ? order : "asc"}
                      onClick={() => requestSort("customerName")}
                    >
                      ชื่อซัพพลายเออร์
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={cellStyle}>สั่งจ่ายโดย</TableCell>

                  {/* น้ำหนักส่งสุทธิ / หัก / ปลายทางสุทธิ (sum จาก sub) */}
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "weightSend" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "weightSend"}
                      direction={orderBy === "weightSend" ? order : "desc"}
                      onClick={() => requestSort("weightSend")}
                    >
                      น้ำหนักส่งสุทธิ
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "weightDeduct" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "weightDeduct"}
                      direction={orderBy === "weightDeduct" ? order : "desc"}
                      onClick={() => requestSort("weightDeduct")}
                    >
                      น้ำหนักหัก
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "weightDest" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "weightDest"}
                      direction={orderBy === "weightDest" ? order : "desc"}
                      onClick={() => requestSort("weightDest")}
                    >
                      น้ำหนักปลายทางสุทธิ
                    </TableSortLabel>
                  </TableCell>

                  {/* เงิน */}
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "beforeVat" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "beforeVat"}
                      direction={orderBy === "beforeVat" ? order : "desc"}
                      onClick={() => requestSort("beforeVat")}
                    >
                      ยอดขายก่อนภาษี
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "vat" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "vat"}
                      direction={orderBy === "vat" ? order : "desc"}
                      onClick={() => requestSort("vat")}
                    >
                      ยอดภาษี
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                    sortDirection={orderBy === "grandTotal" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "grandTotal"}
                      direction={orderBy === "grandTotal" ? order : "desc"}
                      onClick={() => requestSort("grandTotal")}
                    >
                      ยอดรวม
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.map((row, idx) => {
                  const key = getRowKey(row, idx);
                  const isOpen = openSet.has(key);

                  const {
                    weightSend,
                    weightDeduct,
                    weightDest,
                    beforeVat,
                    vat,
                    grandTotal,
                  } = getRowMetrics(row);

                  return (
                    <>
                      <TableRow key={key} hover>
                        <TableCell sx={{ ...cellStyle, paddingBlock: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => toggleOpen(key)}
                            aria-label="expand row"
                          >
                            {isOpen ? (
                              <KeyboardArrowDownIcon />
                            ) : (
                              <KeyboardArrowRightIcon />
                            )}
                          </IconButton>
                        </TableCell>

                        <TableCell sx={cellStyle}>{row.billNumber}</TableCell>
                        <TableCell sx={cellStyle}>
                          {formatDateTime(row.createdAt)}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {row.customer?.name ?? row.customerName ?? "-"}
                        </TableCell>
                        <TableCell sx={cellStyle}>
                          {row.orderedBy?.name ??
                            row.createdBy?.name ??
                            row.shop?.name ??
                            "-"}
                        </TableCell>

                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(weightSend)}
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(weightDeduct)}
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(weightDest)}
                        </TableCell>

                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(beforeVat)}
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(vat)}
                        </TableCell>
                        <TableCell align="right" sx={cellStyle}>
                          {formatNum(grandTotal)}
                        </TableCell>
                      </TableRow>

                      {/* Sub items */}
                      <TableRow>
                        <TableCell style={{ padding: 0 }} colSpan={12}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box p={2}>
                              <Box
                                mb={1}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={1}
                                flexWrap="wrap"
                              >
                                <Typography variant="subtitle2">
                                  รายการสินค้า
                                </Typography>
                                <Button
                                  variant="outlined"
                                  size="large"
                                  startIcon={<ContentCopyIcon />}
                                  onClick={() => {
                                    const dataset = (row.items || []).map((p) => {
                                      const sendW = Number(p.sendWeight ?? p.netWeight ?? 0);
                                      const destW = Number(p.netWeight ?? 0);
                                      const deductW = sendW - destW;
                                      const amountBefore =
                                        Number(p.amountBeforeVat ?? destW * (p.pricePerUnit ?? 0));
                                      const vat = Number(p.vat ?? amountBefore * TAX_RATE);
                                      const total = amountBefore + vat;
                                      return {
                                        เลขที่Batch: row.billNumber ?? "",
                                        วันที่จ่าย: formatDateTime(row.createdAt),
                                        ชื่อซัพพลายเออร์:
                                          row.customer?.name ?? row.customerName ?? "-",
                                        สินค้า: p.productName ?? "-",
                                        "น้ำหนักส่งสุทธิ": sendW.toFixed(2),
                                        "น้ำหนักหัก": deductW.toFixed(2),
                                        "น้ำหนักปลายทางสุทธิ": destW.toFixed(2),
                                        "ราคาขาย/หน่วย": Number(p.pricePerUnit ?? 0).toFixed(2),
                                        "ราคายืนยัน/หน่วย": Number(p.confirmPricePerUnit ?? 0).toFixed(2),
                                        "ยอดขายก่อนภาษี": amountBefore.toFixed(2),
                                        "ยอดภาษี": vat.toFixed(2),
                                        "ยอดรวม": total.toFixed(2),
                                      };
                                    });

                                    // export CSV ย่อย
                                    const headers = Object.keys(dataset[0] ?? {});
                                    const rowsCsv = dataset.map((r) =>
                                      headers.map((h) => `"${r[h] ?? ""}"`).join(",")
                                    );
                                    const csv = [headers.join(","), ...rowsCsv].join("\n");
                                    const blob = new Blob(["\uFEFF" + csv], {
                                      type: "text/csv;charset=utf-8;",
                                    });
                                    const link = document.createElement("a");
                                    link.href = URL.createObjectURL(blob);
                                    link.setAttribute(
                                      "download",
                                      `ticket_${row.billNumber || row.id || "detail"}.csv`
                                    );
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                  }}
                                >
                                  โหลดข้อมูล
                                </Button>
                              </Box>

                              <Table>
                                <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                                  <TableRow>
                                    <TableCell sx={cellStyle}>สินค้า</TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      น้ำหนักส่งสุทธิ
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      น้ำหนักหัก
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      น้ำหนักปลายทางสุทธิ
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      ราคาขาย/หน่วย
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      ราคายืนยัน/หน่วย
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      ยอดขายก่อนภาษี
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      ยอดภาษี
                                    </TableCell>
                                    <TableCell align="right" sx={cellStyle}>
                                      ยอดรวม
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(row.items ?? []).map((p, i) => {
                                    const sendW = Number(p.sendWeight ?? p.netWeight ?? 0);
                                    const destW = Number(p.netWeight ?? 0);
                                    const deductW = sendW - destW;
                                    const amountBefore =
                                      Number(p.amountBeforeVat ?? destW * (p.pricePerUnit ?? 0));
                                    const vat = Number(p.vat ?? amountBefore * TAX_RATE);
                                    const total = amountBefore + vat;

                                    return (
                                      <TableRow key={`item-${row.billNumber ?? i}-${i}`}>
                                        <TableCell sx={cellStyle}>
                                          {p.productName ?? "-"}
                                        </TableCell>
                                        <TableCell align="right" sx={cellStyle}>
                                          {formatNum(sendW)}
                                        </TableCell>
                                        <TableCell align="right" sx={cellStyle}>
                                          {formatNum(deductW)}
                                        </TableCell>
                                        <TableCell align="right" sx={cellStyle}>
                                          {formatNum(destW)}
                                        </TableCell>
                                        <TableCell align="right" sx={cellStyle}>
                                          {formatNum(p.pricePerUnit)}
                                        </TableCell>
                                        <TableCell align="right" sx={cellStyle}>
                                          {formatNum(p.confirmPricePerUnit)}
                                        </TableCell>
                                        <TableCell align="right" sx={cellStyle}>
                                          {formatNum(amountBefore)}
                                        </TableCell>
                                        <TableCell align="right" sx={cellStyle}>
                                          {formatNum(vat)}
                                        </TableCell>
                                        <TableCell align="right" sx={cellStyle}>
                                          {formatNum(total)}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>

              <TableFooter>
                <TableRow
                  sx={{
                    backgroundColor: "#fafafa",
                    "& td": { fontSize: "14px", fontWeight: "bold" },
                  }}
                >
                  <TableCell sx={cellStyle} />
                  <TableCell sx={cellStyle} colSpan={3}>
                    ยอดรวม {summary.ticketCount.toLocaleString("th-TH")} Batch
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.weightSend)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.weightDeduct)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.weightDest)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalBeforeVat)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.totalVat)}
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    {formatNum(summary.grandTotal)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
